import { TranscriptEntry } from '../store/conversationSlice';

/**
 * Client-side speech-rate readout.
 *
 * WHY THIS EXISTS AT ALL, given the websocket services compute the same thing:
 * this runs every two seconds off the transcript list already in the store, so
 * the on-screen number moves between server messages instead of stepping
 * whenever a final transcript happens to arrive. It is a READOUT. The server's
 * `speech_rate_alert` remains the authority on whether a rep is actually
 * warned.
 *
 * IT MUST THEREFORE AGREE WITH THE SERVER, and it did not. Both implemented
 * the same two defects (see services/utils/speech_rate.py for the full
 * account):
 *
 *  - the threshold was a WORD COUNT in a window rather than a rate, so the
 *    same nominal WPM behaved differently at different window lengths;
 *  - words per minute divided by the NOMINAL window, silence included, so a
 *    rep who said 25 words in three seconds and stopped read as 150 WPM. The
 *    burstiest speakers scored lowest, which is backwards.
 *
 * Both are fixed here in the same shape as the Python, deliberately: two
 * implementations of one number can only be kept honest if they are the same
 * algorithm written twice, and `speechRateService.test.ts` pins the cases the
 * Python suite pins.
 */

/** Recency bound on "current pace". Not user-configurable — see the model. */
export const WINDOW_SECONDS = 30;

/** Below this there is not enough speech for the quotient to mean anything. */
export const MIN_SAMPLE_SECONDS = 6;
export const MIN_SAMPLE_WORDS = 12;

/**
 * THE FRONTEND GUARD (owner, 2026-09-09): at least two minutes between speech
 * rate alerts. A rep who is talking fast is still talking fast eight seconds
 * later, and a second card tells them nothing while taking the screen away
 * from the landmine and competitor alerts, which carry information they cannot
 * get anywhere else. The server enforces the same interval independently; this
 * is the half that governs what the UI actually renders.
 */
export const ALERT_REFRACTORY_MS = 2 * 60 * 1000;

export interface SpeechRateAnalysis {
  /** Null when there is not enough speech yet — never 0, which would be a lie. */
  words_per_minute: number | null;
  /** True only when over threshold AND outside the refractory period. */
  is_too_fast: boolean;
  /** Over threshold, regardless of whether the alert is being suppressed. */
  is_over_threshold: boolean;
  wpm_threshold: number;
  total_words: number;
  recent_transcripts: TranscriptEntry[];
}

export class SpeechRateService {
  private static instance: SpeechRateService;
  private analysisInterval: NodeJS.Timeout | null = null;
  private lastAlertAt: number | null = null;

  private constructor() {}

  static getInstance(): SpeechRateService {
    if (!SpeechRateService.instance) {
      SpeechRateService.instance = new SpeechRateService();
    }
    return SpeechRateService.instance;
  }

  private countWords(text?: string): number {
    if (!text || typeof text !== 'string') return 0;
    return text.trim().split(/\s+/).filter((word) => word.length > 0).length;
  }

  private salespersonTranscriptsInWindow(
    transcripts: TranscriptEntry[],
    now: number
  ): TranscriptEntry[] {
    const cutoff = now - WINDOW_SECONDS * 1000;
    return (transcripts ?? []).filter((transcript) => {
      if (transcript?.speaker !== 'salesperson') return false;
      const at = new Date(transcript.timestamp).getTime();
      return Number.isFinite(at) && at >= cutoff;
    });
  }

  /**
   * Conversational pace over the OBSERVED span of the retained transcripts.
   *
   * The span runs from the oldest retained entry to `now`, not the nominal
   * window — that difference is the whole correction. Pauses between
   * utterances still count, deliberately: the 120-160 WPM figure everyone
   * quotes for comfortable speech is itself a conversational-pace number.
   */
  analyzeSpeechRate(
    transcripts: TranscriptEntry[],
    wpmThreshold: number,
    nowMs: number = Date.now()
  ): SpeechRateAnalysis {
    const recent = this.salespersonTranscriptsInWindow(transcripts, nowMs);
    const totalWords = recent.reduce((sum, t) => sum + this.countWords(t?.text), 0);

    const oldest = recent.length > 0 ? new Date(recent[0].timestamp).getTime() : nowMs;
    const spanSeconds = (nowMs - oldest) / 1000;

    const hasEnough = spanSeconds >= MIN_SAMPLE_SECONDS && totalWords >= MIN_SAMPLE_WORDS;
    const wordsPerMinute = hasEnough ? Math.round((totalWords / spanSeconds) * 60) : null;
    const isOverThreshold = wordsPerMinute !== null && wordsPerMinute > wpmThreshold;

    let isTooFast = false;
    if (isOverThreshold) {
      const sinceLast = this.lastAlertAt === null ? Infinity : nowMs - this.lastAlertAt;
      if (sinceLast >= ALERT_REFRACTORY_MS) {
        this.lastAlertAt = nowMs;
        isTooFast = true;
      }
    }

    return {
      words_per_minute: wordsPerMinute,
      is_too_fast: isTooFast,
      is_over_threshold: isOverThreshold,
      wpm_threshold: wpmThreshold,
      total_words: totalWords,
      recent_transcripts: recent,
    };
  }

  startMonitoring(
    getTranscripts: () => TranscriptEntry[],
    getSettings: () => { wpm_threshold: number },
    onAnalysis: (analysis: SpeechRateAnalysis) => void,
    intervalMs: number = 2000
  ): void {
    this.stopMonitoring();
    this.analysisInterval = setInterval(() => {
      onAnalysis(this.analyzeSpeechRate(getTranscripts(), getSettings()?.wpm_threshold ?? 190));
    }, intervalMs);
  }

  stopMonitoring(): void {
    if (this.analysisInterval) {
      clearInterval(this.analysisInterval);
      this.analysisInterval = null;
    }
    // The refractory clock is dropped with the session. Carrying it into the
    // next call would silence that call's first alert for no reason.
    this.lastAlertAt = null;
  }

  getSpeechRateSummary(analysis: SpeechRateAnalysis): string {
    if (analysis.words_per_minute === null) {
      return 'Listening…';
    }
    return `${analysis.words_per_minute} wpm - ${analysis.is_over_threshold ? 'Fast' : 'Normal'}`;
  }

  /** Colour tracks the PACE, not the alert — the readout stays honest while
   *  the alert itself is inside its refractory period. */
  getSpeechRateColor(analysis: SpeechRateAnalysis): string {
    if (analysis.is_over_threshold) return 'bg-error';
    return analysis.words_per_minute !== null ? 'bg-success' : 'bg-ink-muted';
  }
}

export const speechRateService = SpeechRateService.getInstance();
